import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Ppe } from 'src/app/models/ppe.model';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { PpeService } from 'src/app/services/ppe.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { PpeDialogComponent } from 'src/app/modals/ppe-dialog/ppe-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { PpeType } from 'src/app/enums/ppeType.enum';
import { RatingTypeEnum } from 'src/app/enums/ratingTypeEnum.enum';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { AuthService } from 'src/app/services';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { Observable, of } from 'rxjs';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { Lookup } from 'src/app/models/lookup.model';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';

@Component({
  standalone: false,
  selector: 'app-ppe-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss']
})
export class PpeListComponent extends CoreGridComponent<Ppe> implements OnInit, AfterViewInit {

  organizationLevels: Lookup[] = [];

  constructor(
    protected ppeService: PpeService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private organizationLevelService: OrganizationLevelService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService,
      dialog,
      commonService,
      authService,
      gridViewService,
      gridSettingsService
    );
    this.gridService = ppeService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.Ppe;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  protected override get deferGridViewInit(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'PPE';
    this.searchPlaceholder = 'Search PPEs';
  }

  override initializeColumns(): void {
    this.additionalActions = [];
    this.columnConfigs = [
      { name: nameOf<Ppe>(x => x.name), displayName: 'Name' },
      {
        name: nameOf<Ppe>(x => x.type),
        displayName: 'Type',
        imgSrcs: (row) => {
          if (row.type === PpeType.Respiratory) return ['assets/images/mask.svg'];
          if (row.type === PpeType.Noise) return ['assets/images/hearing.svg'];
          return [];
        },
        cellTemplate: (row) => ''
      },
      {
        name: nameOf<Ppe>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (row) => this.organizationLevels?.find(org => org.id === row.organizationLevelId)?.name || '-'
      },
      { name: nameOf<Ppe>(x => x.fileName), displayName: 'File Name' },
      { name: nameOf<Ppe>(x => x.brand), displayName: 'Brand' },
      { name: nameOf<Ppe>(x => x.model), displayName: 'Model' },
      { name: nameOf<Ppe>(x => x.rating), displayName: 'Rating' },
      { name: nameOf<Ppe>(x => x.ratingType), displayName: 'Rating Type', cellTemplate: (row) => row.ratingType != null ? RatingTypeEnum[row.ratingType] : '-', enumFilterMap: this.enumToValueNameArray(RatingTypeEnum) },
      { name: 'action', displayName: 'Actions' }
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<Ppe>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  get hasOrgFilter() {
    return true;
  }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<PpeListComponent>, null);
    super.ngOnInit();
    this.organizationLevelService.getAll().subscribe(orgs => this.organizationLevels = orgs);
    this.startGridViewInitialization();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(ppeId?: string): void {
    const ppeModel: IFormDialogData = {
      id: ppeId
    };
    this.openDialog(PpeDialogComponent, ppeModel);
  }

  override openEntityDeleteDialog(ppe: Ppe): void {
    this.openDeleteDialog(DeleteDialog, ppe, this.ppeService, 'PPE');
  }
}
